import React, { useState, useRef, useEffect } from 'react';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Copy, BringToFront, SendToBack, Edit2, ExternalLink } from 'lucide-react';
import { useCanvas } from '../context/CanvasContext';
import 'react-resizable/css/styles.css';

const BookmarkBlock = ({ block }) => {
  const { mode, updateBlock, removeBlock, addBlock, snapToGrid, bringToFront, sendToBack, zoom, selectedBlockId, setSelectedBlockId } = useCanvas();
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const nodeRef = useRef(null);
  const [position, setPosition] = useState(block.position);
  
  const isSelected = selectedBlockId === block.id;

  useEffect(() => {
    setPosition(block.position);
  }, [block.position]);

  const handleDragStop = (e, data) => {
    const newPos = { x: data.x, y: data.y };
    setPosition(newPos);
    updateBlock(block.id, { position: newPos });
  };

  const handleResize = (e, { size }) => {
    updateBlock(block.id, { size });
  };

  const handleDuplicate = () => {
    addBlock('bookmark', { ...block.data }, { x: position.x + 20, y: position.y + 20 });
  };

  const toggleEditContent = () => {
    setIsEditingContent(!isEditingContent);
  };

  const handleContentChange = (field, value) => {
    updateBlock(block.id, { data: { ...block.data, [field]: value } });
  };

  const getFavicon = (url) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
    } catch (e) {
      return '';
    }
  };
  
  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
    }
    setIsHovered(true);
  };


  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
        setIsHovered(false);
    }, 600); // Increased delay
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
    };
  }, []);

  // ...

  const cardContent = (
    <motion.div
      className="bookmark-card"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onPointerDown={(e) => {
        // e.stopPropagation() is handled by Draggable, but we need to ensure selection happens
        // Since Draggable handles mousedown, we might need to be careful.
        // But clicking on content inside draggable usually propagates unless stopped.
        // We want to select when clicked.
        setSelectedBlockId(block.id);
      }}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'var(--card-bg)',
        border: isSelected ? '2px solid var(--text-color)' : '1px solid var(--border-color)',
        borderRadius: '12px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        boxShadow: isHovered || isSelected ? '0 8px 30px var(--shadow-color)' : '0 2px 10px var(--shadow-color)',
        transition: 'box-shadow 0.3s ease, border 0.2s ease',
        position: 'relative',
        color: 'var(--text-color)',
      }}
    >
        <AnimatePresence>
          {mode === 'edit' && (isHovered || isSelected) && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              style={{
                position: 'absolute',
                top: '-40px',
                left: '50%',
                x: '-50%',
                scale: 1 / zoom,
                transformOrigin: 'bottom center',
                display: 'flex',
                gap: '8px',
                background: 'var(--bg-color)',
                padding: '6px 12px',
                borderRadius: '20px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                zIndex: 1000,
                border: '1px solid var(--border-color)'
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button className="icon-btn" onClick={toggleEditContent} title="Edit Content"><Edit2 size={14} /></button>
              <button className="icon-btn" onClick={handleDuplicate} title="Duplicate"><Copy size={14} /></button>
              <button className="icon-btn" onClick={() => removeBlock(block.id)} title="Delete" style={{ color: 'red' }}><Trash2 size={14} /></button>
            </motion.div>
          )}
        </AnimatePresence>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          {block.data.url && (
            <img 
              src={getFavicon(block.data.url)} 
              alt="favicon" 
              style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'contain' }} 
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          {isEditingContent ? (
            <input
              type="text"
              value={block.data.title || ''}
              onChange={(e) => handleContentChange('title', e.target.value)}
              placeholder="Title"
              className="minimal-input title-input"
              style={{ 
                width: '100%', 
                background: 'rgba(255,255,255,0.1)', 
                border: '1px solid var(--border-color)', 
                borderRadius: '4px', 
                padding: '4px', 
                color: 'var(--text-color)',
                fontSize: '16px',
                fontWeight: 600
              }}
              autoFocus
            />
          ) : (
            <h3 
                style={{ margin: 0, fontSize: '16px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: mode === 'edit' ? 'text' : 'pointer' }}
                onClick={(e) => {
                    if (mode === 'edit' && !isEditingContent) {
                        e.stopPropagation();
                        toggleEditContent();
                    }
                }}
            >
              {block.data.title || 'Untitled'}
            </h3>
          )}
        </div>

        {isEditingContent ? (
          <textarea
            value={block.data.description || ''}
            onChange={(e) => handleContentChange('description', e.target.value)}
            placeholder="Description"
            className="minimal-input desc-input"
            style={{ 
                flex: 1, 
                resize: 'none', 
                width: '100%', 
                background: 'rgba(255,255,255,0.1)', 
                border: '1px solid var(--border-color)', 
                borderRadius: '4px', 
                padding: '4px', 
                color: 'var(--text-color)',
                fontSize: '14px',
                fontFamily: 'inherit'
            }}
          />
        ) : (
          <p 
            style={{ margin: 0, fontSize: '14px', color: 'gray', flex: 1, overflow: 'hidden', cursor: mode === 'edit' ? 'text' : 'default' }}
            onClick={(e) => {
                if (mode === 'edit' && !isEditingContent) {
                    e.stopPropagation();
                    toggleEditContent();
                }
            }}
          >
            {block.data.description}
          </p>
        )}

        {isEditingContent ? (
           <input
              type="text"
              value={block.data.url || ''}
              onChange={(e) => handleContentChange('url', e.target.value)}
              placeholder="https://example.com"
              className="minimal-input url-input"
              style={{ 
                width: '100%', 
                background: 'rgba(255,255,255,0.1)', 
                border: '1px solid var(--border-color)', 
                borderRadius: '4px', 
                padding: '4px', 
                color: 'var(--text-color)',
                fontSize: '12px',
                marginTop: 'auto'
              }}
            />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 'auto', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
              <span style={{ fontSize: '12px', color: 'gray', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '85%' }}>
                {block.data.url}
              </span>
              <a href={block.data.url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto', color: 'var(--text-color)' }}>
                <ExternalLink size={14} />
              </a>
          </div>
        )}
    </motion.div>
  );

  if (mode === 'view') {
    return (
      <div
        style={{
          position: 'absolute',
          left: position.x,
          top: position.y,
          width: block.size.width,
          height: block.size.height,
          zIndex: block.zIndex,
        }}
        onClick={() => {
           if(block.data.url) window.open(block.data.url, '_blank');
        }}
      >
        <div style={{ width: '100%', height: '100%', pointerEvents: 'none' }}>
             <motion.div
                className="bookmark-card view-mode"
                whileHover={{ y: -5, boxShadow: '0 12px 40px var(--shadow-color)' }}
                style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    boxShadow: '0 2px 10px var(--shadow-color)',
                    color: 'var(--text-color)',
                    cursor: 'pointer'
                }}
                >
                 <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    {block.data.url && (
                        <img 
                        src={getFavicon(block.data.url)} 
                        alt="favicon" 
                        style={{ width: '24px', height: '24px', borderRadius: '4px' }} 
                        />
                    )}
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {block.data.title || 'Untitled'}
                    </h3>
                </div>
                 <p style={{ margin: 0, fontSize: '14px', color: 'gray', flex: 1, overflow: 'hidden' }}>
                    {block.data.description}
                </p>
            </motion.div>
        </div>
      </div>
    );
  }

  return (
    <Draggable
      nodeRef={nodeRef}
      position={position}
      onStop={handleDragStop}
      grid={snapToGrid ? [20, 20] : null}
      scale={zoom}
      cancel=".icon-btn, input, textarea, .react-resizable-handle, .minimal-input"
    >
      <div
        ref={nodeRef}
        style={{
          position: 'absolute',
          zIndex: isHovered ? 999999 : block.zIndex,
        }}
      >
        <ResizableBox
          width={block.size.width}
          height={block.size.height}
          onResizeStop={handleResize}
          minConstraints={[200, 100]}
          maxConstraints={[600, 600]}
          resizeHandles={['se']}
          handle={
             <span className="react-resizable-handle react-resizable-handle-se" style={{ opacity: isHovered ? 1 : 0 }} />
          }
        >
          {cardContent}
        </ResizableBox>
      </div>
    </Draggable>
  );
};

export default BookmarkBlock;
